import { BeneficiaryType, Currency } from '@seapoint/server/enums';
import { Avatar } from '../../../../design-system/avatar/avatar/avatar';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { cn } from '../../../../utils/cn';
import { getJointAvatarDescription } from '../invoice-data-section/utils/get-joint-avatar-description';

export type BeneficiaryCardVariant = 'SUGGESTED_BENEFICIARY' | 'NEW_BENEFICIARY';

type VariantConfig =
  | {
      variant: Extract<BeneficiaryCardVariant, 'SUGGESTED_BENEFICIARY'>;
      beneficiaryType: BeneficiaryType;
      currency: Currency;
      iban?: string;
      accountNumber?: string;
      sortCode?: string;
    }
  | {
      variant: Extract<BeneficiaryCardVariant, 'NEW_BENEFICIARY'>;
    };

type InvoiceDrawerBeneficiaryCardProps = {
  variantConfig: VariantConfig;
  name: string;
};

export const InvoiceDrawerBeneficiaryCard = ({ variantConfig, name }: InvoiceDrawerBeneficiaryCardProps) => {
  const { description } = getCardContent(variantConfig);

  return (
    <Inline space={3} className="w-full rounded-[10px] border-[0.5px] border-seapoint-gray-border bg-white p-4">
      <Avatar name={name} size="sm" />

      <Stack space={0} className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-6 tracking-tight text-black">{name}</p>

        <p
          className={cn('text-xs font-normal leading-6 tracking-tight text-seapoint-gray-text', {
            'text-seapoint-orange': variantConfig.variant === 'NEW_BENEFICIARY',
          })}
        >
          {description}
        </p>
      </Stack>
    </Inline>
  );
};

const getCardContent = (variantConfig: VariantConfig) => {
  switch (variantConfig.variant) {
    case 'SUGGESTED_BENEFICIARY': {
      const description = getJointAvatarDescription({
        item1: variantConfig.currency,
        item2:
          variantConfig.beneficiaryType === BeneficiaryType.IBAN
            ? variantConfig.iban
            : `${variantConfig.accountNumber} · ${variantConfig.sortCode}`,
      });

      return {
        description,
      };
    }
    case 'NEW_BENEFICIARY':
      return {
        description: 'New Beneficiary',
      };
  }
};
